import React, { useState, useEffect, useMemo } from "react";
import { connect } from "react-redux";
import { Actions as LOGINACTIONS } from "../../store/actions/authenticationActions";
import Select from "react-select";
import XeroLogoBlue from "./xero-logo-blue.svg";
import BuddyFace from "../../assets/images/buddy-face.png";
import FacebookLogin from "react-facebook-login";
import { format } from "date-fns";

import { Link, useLocation } from "react-router-dom";
import { NotificationManager } from "react-notifications";

import { ALLOWED_HANDOFF_ORIGINS, appUrl } from "../../utils/appHandoff";

const Login = ({
	profile,
	login,
	resetPassword,
	invalidLoginCredentials,
	processing,
	logout,
	sso,
	goToZendesk,
	externalAppLogin,
	createHandoffCode,
	joinOrder,
	formFooter,
	accountLockedUntil,
}) => {
	const [reset, setReset] = useState(false);
	const [username, setUsername] = useState();
	const [password, setPassword] = useState();

	const isJoint = !isNaN(joinOrder);

	const location = useLocation();
	const referrer = useMemo(() => {
		const referrer = new URLSearchParams(location.search).get("referrer");

		if (referrer === "https://www.buddy.hr/salaryCalculatorEmbed.html") return "/cola-report";
		else return referrer;
	}, [location.search]);

	useEffect(() => {
		if (profile && sso) {
			goToZendesk();
		}
	}, [profile?.id, sso]);

	const externalLogin = ({ email, externalId, token }) => {
		externalAppLogin({ email, externalId, appName: "facebook", token });
	};

	const redirectWithHandoff = async (portal, baseUrl) => {
		try {
			// Only allow redirects to trusted Buddy origins to prevent open-redirect / XSS
			// via an attacker-controlled baseUrl (e.g. a javascript: scheme or foreign host).
			const target = new URL(`${baseUrl}/auth/handoff`, window.location.origin);
			if (!ALLOWED_HANDOFF_ORIGINS.includes(target.origin)) {
				throw new Error("Untrusted handoff destination");
			}

			const code = await createHandoffCode(portal);
			if (!code) {
				throw new Error("Missing handoff code");
			}
			target.searchParams.set("code", code);
			window.location.href = target.toString();
		} catch (error) {
			NotificationManager.error("Unable to start secure handoff. Please try again.", "Error");
		}
	};

	if (profile) {
		return (
			<div className="bg-gray-50 flex flex-col justify-center">
				<div
					className={`${
						isJoint ? `ml-${joinOrder % 2 ? "0" : "auto"} mr-${joinOrder % 2 ? "auto" : "0"}` : "sm:mx-auto"
					} sm:w-full sm:max-w-md`}
				>
					<div
						className={`bg-white py-8 px-4
                        ${
							isJoint
								? joinOrder % 2
									? "rounded-r-md sm:rounded-r-lg"
									: "rounded-l-md sm:rounded-l-lg"
								: "rounded-md sm:rounded-lg"
						}sm:px-10`}
					>
						{(profile.isHR || (!profile.isManager && !profile.isHR && !(profile.isEmployee > 0))) && (
							<div>
								<span className="block w-full rounded-md shadow-sm">
									<a
										href={appUrl()}
										onClick={(event) => {
											event.preventDefault();
											redirectWithHandoff("app", appUrl());
										}}
										className="w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-orange-600 hover:bg-orange-500 focus:outline-none focus:border-orange-700 focus:shadow-outline-orange active:bg-orange-700 transition duration-150 ease-in-out"
									>
										Go to Payroll Portal
									</a>
								</span>
							</div>
						)}

						{profile.isManager && (
							<div className="mt-6">
								<span className="block w-full rounded-md shadow-sm">
									<a
										href={appUrl("/manager")}
										onClick={(event) => {
											event.preventDefault();
											redirectWithHandoff("app", appUrl("/manager"));
										}}
										className="w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-orange-600 hover:bg-orange-500 focus:outline-none focus:border-orange-700 focus:shadow-outline-orange active:bg-orange-700 transition duration-150 ease-in-out"
									>
										Go to Manager Portal
									</a>
								</span>
							</div>
						)}

						{profile.isEmployee > 0 && (
							<div className="mt-6">
								<span className="block w-full rounded-md shadow-sm">
									<a
										href={appUrl("/employee")}
										onClick={(event) => {
											event.preventDefault();
											redirectWithHandoff("employee", appUrl("/employee"));
										}}
										className="w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-orange-600 hover:bg-orange-500 focus:outline-none focus:border-orange-700 focus:shadow-outline-orange active:bg-orange-700 transition duration-150 ease-in-out"
									>
										Go to Employee Portal
									</a>
								</span>
							</div>
						)}

						{referrer && (
							<div className="mt-6">
								<span className="block w-full rounded-md shadow-sm">
									<Link
										to={referrer}
										className="w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-orange-600 hover:bg-orange-500 focus:outline-none focus:border-orange-700 focus:shadow-outline-orange active:bg-orange-700 transition duration-150 ease-in-out"
									>
										Go Back
									</Link>
								</span>
							</div>
						)}

						<div className="mt-6 flex items-center justify-between">
							<div className="text-sm leading-5">
								Not {profile.givenName || profile.name}?
								<a
									onClick={(e) => {
										e.preventDefault();
										e.stopPropagation();
										logout();
									}}
									href="#"
									className="ml-1 pl-2 font-medium text-orange-600 hover:text-orange-500 focus:outline-none focus:underline transition ease-in-out duration-150"
								>
									Log out
									{processing && (
										<span className="ml-4">
											<svg
												className="text-orange-600 inline-block animate-spin -ml-1 mr-3 h-5 w-5 text-white"
												xmlns="http://www.w3.org/2000/svg"
												fill="none"
												viewBox="0 0 24 24"
											>
												<circle
													className="opacity-25"
													cx="12"
													cy="12"
													r="10"
													stroke="currentColor"
													strokeWidth="4"
												></circle>
												<path
													className="opacity-75"
													fill="currentColor"
													d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
												></path>
											</svg>
										</span>
									)}
								</a>
							</div>
						</div>
					</div>
				</div>
			</div>
		);
	}

	if (reset) {
		return (
			<div className="bg-gray-50 flex flex-col justify-center sm:px-6 lg:px-8">
				<div
					className={`${
						isJoint ? `ml-${joinOrder % 2 ? "0" : "auto"} mr-${joinOrder % 2 ? "auto" : "0"}` : "sm:mx-auto"
					} sm:w-full sm:max-w-md`}
				>
					<img
						className="mx-auto h-12 w-auto"
						src={BuddyFace}
						alt="Buddy"
					/>
					<h2 className="mt-6 text-center text-3xl leading-9 font-extrabold text-gray-900">
						Reset your password
					</h2>
				</div>

				<div
					className={`${
						isJoint ? `ml-${joinOrder % 2 ? "0" : "auto"} mr-${joinOrder % 2 ? "auto" : "0"}` : "sm:mx-auto"
					} sm:w-full sm:max-w-md`}
				>
					<div
						className={`bg-white py-8 px-4 ${
							isJoint
								? joinOrder % 2
									? "rounded-r-md sm:rounded-r-lg"
									: "rounded-l-md sm:rounded-l-lg"
								: "rounded-md sm:rounded-lg"
						} sm:px-10`}
					>
						<form
							action="#"
							method="POST"
						>
							<div>
								<label
									htmlFor="email"
									className="block text-sm font-medium leading-5 text-gray-700"
								>
									Email address
								</label>
								<div className="mt-1 rounded-md shadow-sm">
									<input
										onChange={(e) => setUsername(e.target.value)}
										id="email"
										type="email"
										required
										className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md placeholder-gray-400 focus:outline-none focus:shadow-outline-blue focus:border-blue-300 transition duration-150 ease-in-out sm:text-sm sm:leading-5"
									/>
								</div>
							</div>

							<div className="mt-6">
								<span className="block w-full rounded-md shadow-sm">
									<button
										type="submit"
										className="w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-orange-600 hover:bg-orange-500 focus:outline-none focus:border-orange-700 focus:shadow-outline-orange active:bg-orange-700 transition duration-150 ease-in-out"
										onClick={(e) => {
											e.preventDefault();
											e.stopPropagation();
											resetPassword(username);
										}}
									>
										{processing && (
											<svg
												className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
												xmlns="http://www.w3.org/2000/svg"
												fill="none"
												viewBox="0 0 24 24"
											>
												<circle
													className="opacity-25"
													cx="12"
													cy="12"
													r="10"
													stroke="currentColor"
													strokeWidth="4"
												></circle>
												<path
													className="opacity-75"
													fill="currentColor"
													d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
												></path>
											</svg>
										)}
										Reset Password
									</button>
								</span>
							</div>

							<div className="mt-6 flex items-center justify-between">
								<div className="text-sm leading-5">
									<a
										onClick={(e) => {
											e.preventDefault();
											e.stopPropagation();
											setReset(false);
										}}
										href="#"
										className="font-medium text-orange-600 hover:text-orange-500 focus:outline-none focus:underline transition ease-in-out duration-150"
									>
										Nope, take me back
									</a>
								</div>
							</div>
						</form>
					</div>
				</div>
			</div>
		);
	}

	return (
		<div
			className={`${
				isJoint ? `ml-${joinOrder % 2 ? "0" : "auto"} mr-${joinOrder % 2 ? "auto" : "0"}` : "sm:mx-auto"
			} sm:w-full sm:max-w-md`}
		>
			<div
				className={`bg-white py-8 px-4
            				${
								isJoint
									? joinOrder % 2
										? "rounded-r-md sm:rounded-r-lg"
										: "rounded-l-md sm:rounded-l-lg"
									: "rounded-md sm:rounded-lg"
							}
            sm:px-10`}
			>
				<form
					action="#"
					method="POST"
				>
					<div>
						<label
							htmlFor="email"
							className="block text-sm font-medium leading-5 text-gray-700"
						>
							Email address
						</label>
						<div className="mt-1 rounded-md shadow-sm">
							<input
								onChange={(e) => setUsername(e.target.value)}
								id="email"
								type="email"
								required
								className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md placeholder-gray-400 focus:outline-none focus:shadow-outline-blue focus:border-blue-300 transition duration-150 ease-in-out sm:text-sm sm:leading-5"
							/>
						</div>
					</div>

					<div className="mt-6">
						<label
							htmlFor="password"
							className="block text-sm font-medium leading-5 text-gray-700"
						>
							Password
						</label>
						<div className="mt-1 rounded-md shadow-sm">
							<input
								onChange={(e) => setPassword(e.target.value)}
								id="password"
								type="password"
								required
								className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md placeholder-gray-400 focus:outline-none focus:shadow-outline-blue focus:border-blue-300 transition duration-150 ease-in-out sm:text-sm sm:leading-5"
							/>
						</div>
					</div>

					<div className="mt-6 flex items-center justify-between">
						<div className="flex items-center">
							<input
								id="remember_me"
								type="checkbox"
								className="form-checkbox h-4 w-4 text-orange-600 transition duration-150 ease-in-out"
							/>
							<label
								htmlFor="remember_me"
								className="ml-2 block text-sm leading-5 text-gray-900"
							>
								Remember me
							</label>
						</div>

						<div className="text-sm leading-5">
							<a
								onClick={(e) => {
									e.preventDefault();
									setReset(true);
								}}
								href="#"
								className="font-medium text-orange-600 hover:text-orange-500 focus:outline-none focus:underline transition ease-in-out duration-150"
							>
								Forgot your password?
							</a>
						</div>
					</div>

					{accountLockedUntil && (
						<div className="rounded-md bg-red-50 p-4 mt-5">
							<div className="flex">
								<div className="flex-shrink-0">
									<svg
										className="h-5 w-5 text-red-400"
										xmlns="http://www.w3.org/2000/svg"
										viewBox="0 0 20 20"
										fill="currentColor"
									>
										<path
											fillRule="evenodd"
											d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
											clipRule="evenodd"
										/>
									</svg>
								</div>
								<div className="ml-3">
									<h3 className="text-sm leading-5 font-medium text-red-800">
										Account Locked
									</h3>
									<div className="mt-2 text-sm leading-5 text-red-700">

										Your account is locked until {format(new Date(accountLockedUntil), "do MMM yyyy HH:mm aa")}
										<br />
										Please check your email for instructions on how to unlock your account.
									</div>
								</div>
							</div>
						</div>
					)}
					{invalidLoginCredentials && (
						<div className="rounded-md bg-red-50 p-4 mt-5">
							<div className="flex">
								<div className="flex-shrink-0">
									<svg
										className="h-5 w-5 text-red-400"
										xmlns="http://www.w3.org/2000/svg"
										viewBox="0 0 20 20"
										fill="currentColor"
									>
										<path
											fillRule="evenodd"
											d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
											clipRule="evenodd"
										/>
									</svg>
								</div>
								<div className="ml-3">
									<h3 className="text-sm leading-5 font-medium text-red-800">
										Incorrect email or password
									</h3>
									<div className="mt-2 text-sm leading-5 text-red-700">
										<ul className="list-disc pl-5">
											<li>Are you using the right email address?</li>
											<li>Is the first letter of your email address in lowercase?</li>
											<li>If you are unable to log-in, reset your password below.</li>
										</ul>
									</div>
								</div>
							</div>
						</div>
					)}

					<div className="mt-6">
						<span className="block w-full rounded-md shadow-sm">
							<button
								type="submit"
								className="w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-orange-600 hover:bg-orange-500 focus:outline-none focus:border-orange-700 focus:shadow-outline-orange active:bg-orange-700 transition duration-150 ease-in-out"
								onClick={(e) => {
									e.preventDefault();
									e.stopPropagation();
									login(username, password);
								}}
							>
								{processing && (
									<svg
										className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
										xmlns="http://www.w3.org/2000/svg"
										fill="none"
										viewBox="0 0 24 24"
									>
										<circle
											className="opacity-25"
											cx="12"
											cy="12"
											r="10"
											stroke="currentColor"
											strokeWidth="4"
										></circle>
										<path
											className="opacity-75"
											fill="currentColor"
											d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
										></path>
									</svg>
								)}
								Sign in
							</button>
						</span>
					</div>
				</form>


				{formFooter}

				<div className="mt-6">
					<div className="relative">
						<div className="absolute inset-0 flex items-center">
							<div className="w-full border-t border-gray-300"></div>
						</div>
						<div className="relative flex justify-center text-sm leading-5">
							<span className="px-2 bg-white text-gray-500">Or sign in with</span>
						</div>
					</div>

					<div className="mt-6 gap-3 w-full flex">
						<span className={`${location.pathname == '/login' || true ? "w-full": "w-1/2"} inline-flex rounded-md shadow-sm`}>
							<a
								href="https://api.buddy.hr/v2/xero/connect"
								className="w-full inline-flex justify-center py-1 items-center border border-gray-300 rounded-md bg-white hover:bg-gray-100 focus:outline-none focus:border-blue-300 focus:shadow-outline-blue transition duration-150 ease-in-out"
								aria-label="Sign in with Xero"
							>
								<img
									src={XeroLogoBlue}
									className="h-8"
								/>
							</a>
						</span>
						{location.pathname !== "/login" && false &&
							<span className="block w-1/2 rounded-md shadow-sm">
								<FacebookLogin
									appId="2116278978716420"
									autoLoad={false}
									fields="name,email,picture"
									scope="public_profile,email,user_birthday"
									// onClick={(response) => {
									// 	console.log(response);
									// }}
									callback={(response) => {
										if (response.status !== "unknown") {
											externalLogin({
												email: response.email,
												externalId: response.id || response.userID,
												token: response.accessToken,
											});
										} else {
											NotificationManager.error("Facebook login failed", "Error");
										}
									}}
									icon={<a className="fa fa-facebook fa-lg" />}
									textButton=""
									cssClass="w-full bg-fb-blue hover:bg-fb-blue-dark text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline h-12"
								/>
							</span>

						}
						{/*<div>
	  			            <span className="w-full inline-flex rounded-md shadow-sm">
	  			              <button type="button" className="w-full inline-flex justify-center py-2 px-4 border border-gray-300 rounded-md bg-white text-sm leading-5 font-medium text-gray-500 hover:text-gray-400 focus:outline-none focus:border-blue-300 focus:shadow-outline-blue transition duration-150 ease-in-out" aria-label="Sign in with Twitter">
	  			                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
	  			                  <path d="M6.29 18.251c7.547 0 11.675-6.253 11.675-11.675 0-.178 0-.355-.012-.53A8.348 8.348 0 0020 3.92a8.19 8.19 0 01-2.357.646 4.118 4.118 0 001.804-2.27 8.224 8.224 0 01-2.605.996 4.107 4.107 0 00-6.993 3.743 11.65 11.65 0 01-8.457-4.287 4.106 4.106 0 001.27 5.477A4.073 4.073 0 01.8 7.713v.052a4.105 4.105 0 003.292 4.022 4.095 4.095 0 01-1.853.07 4.108 4.108 0 003.834 2.85A8.233 8.233 0 010 16.407a11.616 11.616 0 006.29 1.84" />
	  			                </svg>
	  			              </button>
	  			            </span>
	  			          </div>
	  
	  			          <div>
	  			            <span className="w-full inline-flex rounded-md shadow-sm">
	  			              <button type="button" className="w-full inline-flex justify-center py-2 px-4 border border-gray-300 rounded-md bg-white text-sm leading-5 font-medium text-gray-500 hover:text-gray-400 focus:outline-none focus:border-blue-300 focus:shadow-outline-blue transition duration-150 ease-in-out" aria-label="Sign in with GitHub">
	  			                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
	  			                  <path fillRule="evenodd" d="M10 0C4.477 0 0 4.484 0 10.017c0 4.425 2.865 8.18 6.839 9.504.5.092.682-.217.682-.483 0-.237-.008-.868-.013-1.703-2.782.605-3.369-1.343-3.369-1.343-.454-1.158-1.11-1.466-1.11-1.466-.908-.62.069-.608.069-.608 1.003.07 1.531 1.032 1.531 1.032.892 1.53 2.341 1.088 2.91.832.092-.647.35-1.088.636-1.338-2.22-.253-4.555-1.113-4.555-4.951 0-1.093.39-1.988 1.029-2.688-.103-.253-.446-1.272.098-2.65 0 0 .84-.27 2.75 1.026A9.564 9.564 0 0110 4.844c.85.004 1.705.115 2.504.337 1.909-1.296 2.747-1.027 2.747-1.027.546 1.379.203 2.398.1 2.651.64.7 1.028 1.595 1.028 2.688 0 3.848-2.339 4.695-4.566 4.942.359.31.678.921.678 1.856 0 1.338-.012 2.419-.012 2.747 0 .268.18.58.688.482A10.019 10.019 0 0020 10.017C20 4.484 15.522 0 10 0z" clipRule="evenodd" />
	  			                </svg>
	  			              </button>
	  			            </span>
	  			          </div>*/}
					</div>

				</div>
			</div>

		</div>
	);
};

const mapStateToProps = (state) => {
	return {
		profile: state.my.profile,
		invalidLoginCredentials: state.authentication.invalidLoginCredentials,
		processing: state.authentication.processing,
		sso: window.location.pathname == "/login/sso",
		accountLockedUntil: state.authentication.accountLockedUntil,
	};
};

const mapDispatchToProps = (dispatch) => ({
	login: (username, password) => dispatch(LOGINACTIONS.login(username, password)),
	resetPassword: (username) => dispatch(LOGINACTIONS.resetPassword(username)),
	logout: () => dispatch(LOGINACTIONS.logout()),
	goToZendesk: () => dispatch(LOGINACTIONS.goToZendesk()),
	externalAppLogin: ({ email, externalId, appName, token }) =>
		dispatch(LOGINACTIONS.externalAppLogin({ email, externalId, appName, token })),
	createHandoffCode: (portal) => dispatch(LOGINACTIONS.createHandoffCode(portal)),
});

export default connect(mapStateToProps, mapDispatchToProps)(Login);
